import { useState, useEffect, Fragment } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { X, Plus, Users, Calendar, Building2, CheckCircle } from 'lucide-react';
import { supabase } from '../lib/supabase';

interface CastingBrief {
  id: string;
  brand: string;
  name: string;
  description: string;
  status: string;
  deadline?: Date | string;
  proposed_influencers?: any[];
  created_at: Date | string;
}

interface AddToCastingModalProps {
  isOpen: boolean;
  onClose: () => void;
  influencerId: string;
  influencerData: {
    username: string;
    display_name?: string;
    profile_image_url?: string;
    followers_count?: number;
    engagement_rate?: number;
    category?: string;
  };
}

export default function AddToCastingModal({
  isOpen,
  onClose,
  influencerId,
  influencerData
}: AddToCastingModalProps) {
  const [castings, setCastings] = useState<CastingBrief[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [addingToCasting, setAddingToCasting] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      loadCastings();
    }
  }, [isOpen]);

  const loadCastings = async () => {
    try {
      setIsLoading(true);
      const { data, error } = await supabase
        .from('casting_briefs')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setCastings(data || []);
    } catch (error) {
      console.error('Error loading castings:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const addToCasting = async (casting: CastingBrief) => {
    try {
      setAddingToCasting(casting.id);

      const proposedInfluencers = casting.proposed_influencers || [];

      const alreadyExists = proposedInfluencers.some(
        (inf: any) => inf.influencerId === influencerId
      );

      if (alreadyExists) {
        alert('Ce créateur est déjà dans ce casting');
        setAddingToCasting(null);
        return;
      }

      const newInfluencer = {
        influencerId: influencerId,
        username: influencerData.username,
        fullName: influencerData.display_name || influencerData.username,
        displayName: influencerData.display_name || influencerData.username,
        profileImageUrl: influencerData.profile_image_url || '',
        followersCount: influencerData.followers_count || 0,
        engagementRate: influencerData.engagement_rate || 0,
        category: influencerData.category || '',
        brandStatus: 'pending',
        status: 'pending',
        addedAt: new Date().toISOString()
      };

      const updatedInfluencers = [...proposedInfluencers, newInfluencer];

      const { error } = await supabase
        .from('casting_briefs')
        .update({
          proposed_influencers: updatedInfluencers,
          updated_at: new Date().toISOString()
        })
        .eq('id', casting.id);

      if (error) throw error;

      alert('Créateur ajouté au casting avec succès !');
      onClose();
    } catch (error) {
      console.error('Error adding to casting:', error);
      alert('Erreur lors de l\'ajout au casting');
    } finally {
      setAddingToCasting(null);
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-700 border border-green-200';
      case 'closed':
        return 'bg-gray-100 text-gray-700 border border-gray-200';
      default:
        return 'bg-yellow-100 text-yellow-700 border border-yellow-200';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'active':
        return 'Actif';
      case 'closed':
        return 'Fermé';
      default:
        return 'Brouillon';
    }
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-3xl transform overflow-hidden rounded-2xl bg-white p-6 shadow-xl transition-all">
                <div className="flex items-center justify-between mb-6">
                  <Dialog.Title className="text-2xl font-bold text-gray-900">
                    Ajouter au casting
                  </Dialog.Title>
                  <button
                    onClick={onClose}
                    className="text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    <X className="w-6 h-6" />
                  </button>
                </div>

                <div className="mb-6 p-4 bg-blue-50 rounded-xl border border-blue-200">
                  <div className="flex items-center gap-4">
                    <img
                      src={influencerData.profile_image_url || `https://ui-avatars.com/api/?name=${influencerData.username}&size=60`}
                      alt={influencerData.username}
                      className="w-12 h-12 rounded-full object-cover"
                    />
                    <div>
                      <div className="font-semibold text-gray-900">
                        {influencerData.display_name || influencerData.username}
                      </div>
                      <div className="text-sm text-gray-600">
                        @{influencerData.username}
                        {influencerData.followers_count && (
                          <span className="ml-3">
                            {influencerData.followers_count.toLocaleString()} followers
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {isLoading ? (
                  <div className="text-center py-12">
                    <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                    <p className="mt-4 text-gray-600">Chargement des castings...</p>
                  </div>
                ) : castings.length === 0 ? (
                  <div className="text-center py-12">
                    <Users className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                    <p className="text-gray-600">Aucun casting disponible</p>
                    <p className="text-sm text-gray-500 mt-2">
                      Créez un casting dans la page Casting pour commencer
                    </p>
                  </div>
                ) : (
                  <div className="space-y-3 max-h-[500px] overflow-y-auto">
                    {castings.map((casting) => (
                      <div
                        key={casting.id}
                        className="border border-gray-200 rounded-xl p-4 hover:border-blue-300 hover:shadow-md transition-all cursor-pointer"
                        onClick={() => addToCasting(casting)}
                      >
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2">
                              <h3 className="font-semibold text-gray-900 text-lg">
                                {casting.name}
                              </h3>
                              <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusBadge(casting.status)}`}>
                                {getStatusLabel(casting.status)}
                              </span>
                            </div>

                            <div className="flex items-center gap-4 text-sm text-gray-600 mb-2">
                              <div className="flex items-center gap-1">
                                <Building2 className="w-4 h-4" />
                                {casting.brand}
                              </div>
                              {casting.deadline && (
                                <div className="flex items-center gap-1">
                                  <Calendar className="w-4 h-4" />
                                  {new Date(casting.deadline).toLocaleDateString('fr-FR')}
                                </div>
                              )}
                              <div className="flex items-center gap-1">
                                <Users className="w-4 h-4" />
                                {casting.proposed_influencers?.length || 0} influenceurs
                              </div>
                            </div>

                            {casting.description && (
                              <p className="text-sm text-gray-600 line-clamp-2">
                                {casting.description}
                              </p>
                            )}
                          </div>

                          <button
                            disabled={addingToCasting === casting.id}
                            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            onClick={(e) => {
                              e.stopPropagation();
                              addToCasting(casting);
                            }}
                          >
                            {addingToCasting === casting.id ? (
                              <>
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                Ajout...
                              </>
                            ) : (
                              <>
                                <Plus className="w-4 h-4" />
                                Ajouter
                              </>
                            )}
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
